import { Button } from "@/modules/shadcn/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import { EvalLimit, EvalTestCaseDetail } from "@repo/schema";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTestCaseForm } from "../CreateTestCase/useTestCaseForm.hook";
import { EvalActionsSection } from "../EvalActions";
import { LogicBuilder } from "./LogicBuilder/LogicBuilder";
import { LogicBuilderState } from "./LogicBuilder/useLogicBuilder.hook";

export type UpdateTestCaseModalProps = {
  projectId: number;
  configId: number;
  testCase: EvalTestCaseDetail;
  /** All limits available for this project (used as the draggable palette) */
  availableLimits?: EvalLimit[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const UpdateTestCaseModal = ({
  projectId,
  configId,
  testCase,
  availableLimits = [],
  open,
  onOpenChange,
}: UpdateTestCaseModalProps) => {
  const { t } = useTranslation(["evaluation", "common"]);
  const builderStateRef = useRef<LogicBuilderState | null>(null);

  const { form, isSubmitting } = useTestCaseForm({
    projectId,
    configId,
    testCaseId: testCase.id,
    initialValues: testCase,
    getLogicNodes: () => builderStateRef.current?.nodes ?? testCase.logicNodes,
    onSuccess: () => onOpenChange(false),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="text-xl font-semibold">
          {t("testCaseForm.editTitle")}
        </DialogTitle>

        <form.AppForm>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="flex flex-col gap-6"
          >
            <div className="flex flex-col gap-3">
              <h6 className="text-sm font-bold">{t("form.general")}</h6>

              <form.AppField
                name="enabled"
                children={(field) => <field.Switch label={t("form.enabled")} />}
              />

              <form.AppField name="name">
                {(field) => (
                  <field.TextInput
                    label={t("form.name")}
                    placeholder={t("testCaseForm.namePlaceholder")}
                  />
                )}
              </form.AppField>
            </div>

            <EvalActionsSection />

            <div className="flex flex-col gap-2">
              <h6 className="text-sm font-bold">{t("testCaseForm.logic")}</h6>
              <LogicBuilder
                testCase={testCase}
                availableLimits={
                  availableLimits.length > 0
                    ? availableLimits
                    : testCase.limits
                }
                builderRef={(state) => {
                  builderStateRef.current = state;
                }}
              />
            </div>

            <div className="flex justify-end gap-2">
              <DialogClose
                render={<Button type="button" variant="outline" size="lg" />}
              >
                {t("common:actions.cancel")}
              </DialogClose>
              <Button type="submit" size="lg" disabled={isSubmitting}>
                {t("common:actions.save")}
              </Button>
            </div>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
};
